<script>
  import { createEventDispatcher, onMount } from 'svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faTimes, faSave } from '@fortawesome/pro-light-svg-icons';

  const dispatch = createEventDispatcher();

  export let editGroup = null;

  let formData = {
    code: '',
    groupName: '',
    description: '',
    active: true
  };

  let errors = {};

  // Initialize form data when component mounts or editGroup changes
  onMount(() => {
    initializeFormData();
  });

  $: if (editGroup !== undefined) {
    initializeFormData();
  }

  function initializeFormData() {
    if (editGroup) {
      console.log('Initializing form with edit data:', editGroup);
      formData = {
        _id: editGroup._id,
        code: editGroup.code || '',
        groupName: editGroup.groupName || '',
        description: editGroup.description || '',
        active: editGroup.active !== undefined ? editGroup.active : true
      };
    } else {
      // Reset form data for new entries
      console.log('Initializing form for new entry');
      formData = {
        code: '',
        groupName: '',
        description: '',
        active: true
      };
    }
  }

  // Debug form data changes
  $: console.log('Form data updated:', formData);

  function validateForm() {
    errors = {};

    if (!formData.code.trim()) {
      errors.code = 'Code is required';
    }

    if (!formData.groupName.trim()) {
      errors.groupName = 'Group name is required';
    }

    return Object.keys(errors).length === 0;
  }

  function handleSubmit() {
    if (!validateForm()) {
      return;
    }

    const submitData = { ...formData };
    
    // Remove _id if it's empty or undefined for new records
    if (!submitData._id) {
      delete submitData._id;
    }

    if (editGroup) {
      dispatch('update', submitData);
    } else {
      dispatch('create', submitData);
    }
  }

  function handleCancel() {
    dispatch('cancel');
  }
</script>

<div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" on:click|stopPropagation>
  <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto" on:click|stopPropagation>
    <div class="p-6">
      <div class="flex items-center justify-between mb-6">
        <h2 class="text-xl font-semibold text-gray-900 dark:text-gray-100">
          {editGroup ? 'Edit Lookup Group' : 'New Lookup Group'}
        </h2>
        <button
          on:click={handleCancel}
          class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
        >
          <FontAwesomeIcon icon={faTimes} class="h-5 w-5" />
        </button>
      </div>

      <form on:submit|preventDefault={handleSubmit} class="space-y-4" key={editGroup?._id || 'new'}>
        <!-- Code -->
        <div>
          <label for="code" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Code *
          </label>
          <input
            id="code"
            type="text"
            bind:value={formData.code}
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 {errors.code ? 'border-red-500' : ''}"
            placeholder="Enter unique code"
            autocomplete="off"
          />
          {#if errors.code}
            <p class="mt-1 text-sm text-red-600 dark:text-red-400">{errors.code}</p>
          {/if}
        </div>

        <!-- Group Name -->
        <div>
          <label for="groupName" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Group Name *
          </label>
          <input
            id="groupName"
            type="text"
            bind:value={formData.groupName}
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 {errors.groupName ? 'border-red-500' : ''}"
            placeholder="Enter group name"
            autocomplete="off"
          />
          {#if errors.groupName}
            <p class="mt-1 text-sm text-red-600 dark:text-red-400">{errors.groupName}</p>
          {/if}
        </div>

        <!-- Description -->
        <div>
          <label for="description" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Description
          </label>
          <textarea
            id="description"
            bind:value={formData.description}
            rows="3"
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
            placeholder="Enter description (optional)"
          ></textarea>
        </div>

        <!-- Active Status -->
        <div>
          <label class="flex items-center">
            <input
              type="checkbox"
              bind:checked={formData.active}
              class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">Active</span>
          </label>
        </div>

        <!-- Action Buttons -->
        <div class="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            on:click={handleCancel}
            class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors flex items-center space-x-1"
          >
            <FontAwesomeIcon icon={faSave} class="h-3 w-3" />
            <span>{editGroup ? 'Update' : 'Create'}</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</div> 